/**
 * Centralized weight display logic
 * 
 * Provides consistent weight formatting across the application
 * based on context, not individual template decisions
 */

import { extractDisplayWeight } from './weightUtils'
import { formatToPrecision } from '@/util/formatting'

/**
 * Display unit policy - single source of truth for what units to show where
 */
const DISPLAY_UNIT_POLICY = {
  // Lot-level weights (reception, processing, aggregates)
  lot: { unit: 'troyOunces', label: 'ozt' },
  bar: { unit: 'troyOunces', label: 'ozt' },
  pin: { unit: 'troyOunces', label: 'ozt' },
  
  // Fire assay weights (cup results, dore results)
  fire: { unit: 'grams', label: 'G' },
  cup: { unit: 'grams', label: 'G' },
  dore: { unit: 'grams', label: 'G' },
  
  // XRF weights (if needed)
  xrf: { unit: 'troyOunces', label: 'ozt' }
}

/**
 * Format a weight value for display with appropriate unit
 * @param {object|number|null} weight - Rich weight object or legacy number
 * @param {string} context - Context hint ('lot', 'bar', 'fire', 'cup', etc.)
 * @param {object} options - Formatting options
 * @returns {object} { value, label, formatted }
 */
export const formatWeight = (weight, context = 'lot', options = {}) => {
  const { decimals = 4, useGrouping = false } = options
  if (options.fallback) console.warn('formatWeight fallback of', options.fallback, 'no longer supported')
  
  // Get display policy for this context
  const policy = DISPLAY_UNIT_POLICY[context] || DISPLAY_UNIT_POLICY.lot
  
  const value = extractDisplayWeight(weight, policy.unit)
  
  const displayValue = formatToPrecision(value, decimals, '--')

  // Optionally add thousands separators to the integer portion
  let groupedDisplayValue = displayValue
  if (useGrouping && displayValue !== '--') {
    const isNegative = groupedDisplayValue.startsWith('-')
    const normalized = isNegative ? groupedDisplayValue.slice(1) : groupedDisplayValue
    const parts = normalized.split('.')
    const intPart = parts[0]
    const decPart = parts[1]
    const groupedInt = Number(intPart).toLocaleString('en-US')
    groupedDisplayValue = isNegative ? `-${groupedInt}` : groupedInt
    if (decPart !== undefined) {
      groupedDisplayValue += `.${decPart}`
    }
  }

  return {
    value: displayValue === '--' ? null : +displayValue,
    label: policy.label,
    formatted: `${groupedDisplayValue} ${policy.label}`
  }
}

/**
 * Format a value with unit, handling empty/null values gracefully
 * @param {any} value - Value to format (number, string, etc.)
 * @param {string} unit - Unit suffix (e.g., 'g', '%', 'oz')
 * @param {object} options - Formatting options
 * @returns {string} Formatted string like "1.23g" or "--"
 */
export const formatValueWithUnit = (value, unit, options = {}) => {
  const { decimals = null } = options
  if (options.fallback) console.warn('formatValueWithUnit fallback of', options.fallback, 'no longer supported')
  
  const displayValue = formatToPrecision(value, decimals, '--')
  
  return `${displayValue}${unit}`
}

/**
 * Get just the display unit info for a context (for form suffixes, etc.)
 * @param {string} context - Context hint
 * @returns {object} { unit, label }
 */
export const getDisplayUnit = (context = 'lot') => {
  return DISPLAY_UNIT_POLICY[context] || DISPLAY_UNIT_POLICY.lot
}

/**
 * Format weight for table cells (common pattern)
 * @param {object|number|null} weight - Weight to format
 * @param {string} context - Context hint
 * @param {number} decimals - Number of decimal places (defaults to 4)
 * @returns {string} Formatted string like "10.5000 ozt" or "-- ozt"
 */
export const formatWeightForTable = (weight, context = 'lot', decimals = 4) => {
  return formatWeight(weight, context, { decimals }).formatted
}

/**
 * Format weight for form inputs (just the numeric value)
 * @param {object|number|null} weight - Weight to format  
 * @param {string} context - Context hint
 * @returns {number|null} Numeric value for form input
 */
export const formatWeightForInput = (weight, context = 'lot') => {
  return formatWeight(weight, context).value
}

/**
 * Vue filter function for templates
 * Usage: {{ weight | weightDisplay('fire') }}
 * @param {object|number|null} weight - Weight to format
 * @param {string} context - Context hint
 * @returns {string} Formatted display string
 */
export const weightDisplayFilter = (weight, context = 'lot', useGrouping = false) => {
  return formatWeight(weight, context, { useGrouping }).formatted
}
